import { createI18n } from 'vue-i18n'
import { Locale } from 'vant'
import enUSVant from 'vant/es/locale/lang/en-US'
import zhCNVant from 'vant/es/locale/lang/zh-CN'
import zhTWVant from 'vant/es/locale/lang/zh-TW'
import trTRVant from 'vant/es/locale/lang/tr-TR'
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES, getBrowserLocale, normalizeLocale } from './localeConfig'

const loadLocaleMessages = () => {
  const messages = {}
  const locales = import.meta.glob('./lang/*.json', { eager: true })

  for (const path in locales) {
    const matched = path.match(/\.\/lang\/(.*)\.json$/)
    if (!matched || matched.length < 2) continue
    const locale = matched[1]
    const localeKey = normalizeLocale(locale)
    if (!SUPPORTED_LANGUAGES.includes(localeKey)) continue
    messages[localeKey] = locales[path].default || locales[path]
  }

  SUPPORTED_LANGUAGES.forEach((lang) => {
    if (!messages[lang]) {
      messages[lang] = messages[DEFAULT_LANGUAGE] || {}
    }
  })

  return messages
}

const messages = loadLocaleMessages()

const VANT_PACK = {
  CN: { code: 'zh-CN', pack: zhCNVant },
  TW: { code: 'zh-TW', pack: zhTWVant },
  TR: { code: 'tr-TR', pack: trTRVant },
  default: { code: 'en-US', pack: enUSVant }
}

SUPPORTED_LANGUAGES.forEach((lang) => {
  const target = VANT_PACK[lang] || VANT_PACK.default
  messages[lang] = messages[lang] || {}
  messages[lang].vant = target.pack
})

// 安全获取localStorage
const safeGetLocalStorage = (key, defaultValue = null) => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      return localStorage.getItem(key) || defaultValue
    }
  } catch (error) {
    console.warn('localStorage不可用:', error)
  }
  return defaultValue
}

// 获取浏览器语言
const getBrowserLang = () => {
  return getBrowserLocale()
}

// 获取初始语言
const getInitialLocale = () => {
  const storedLang = safeGetLocalStorage('lang')
  if (storedLang && messages[storedLang]) {
    return storedLang
  }
  return getBrowserLang()
}

// 确保消息对象不为空
if (!messages || Object.keys(messages).length === 0) {
  console.error('语言包加载失败，使用默认配置')
  // 提供默认的语言包作为备选
  messages.US = {
    common: { confirm: "Confirm", cancel: "Cancel", search: "Search" },
    sharePost: { welcome: "Explore investment strategies, start here!" },
    invite: { title: "Let trading insights be seen", download: "Download App" }
  }
}

const i18n = createI18n({
  legacy: false, // 使用 Composition API 模式
  locale: getInitialLocale(), // 安全获取初始语言
  fallbackLocale: DEFAULT_LANGUAGE, // 备用语言
  messages,
  silentTranslationWarn: true, // 在生产环境减少警告
  silentFallbackWarn: true
})

// 安全配置 Vant 多语言
const initVantLocale = () => {
  try {
    const currentLocale = i18n.global.locale.value || DEFAULT_LANGUAGE
    const vantConfig = VANT_PACK[currentLocale] || VANT_PACK.default
    Locale.use(vantConfig.code, messages[currentLocale]?.vant || vantConfig.pack)
    console.log('Vant多语言初始化成功:', currentLocale)
  } catch (error) {
    console.error('Vant多语言初始化失败:', error)
    // 最后的安全回退
    try {
      Locale.use(VANT_PACK.default.code, VANT_PACK.default.pack)
    } catch (fallbackError) {
      console.error('Vant多语言回退失败:', fallbackError)
    }
  }
}

// 初始化Vant多语言
initVantLocale()

// 导出切换语言的方法
export const switchLanguage = (locale) => {
  const normalized = normalizeLocale(locale)
  if (!messages[normalized]) {
    console.warn(`语言包不存在: ${locale}`)
    return
  }
  i18n.global.locale.value = normalized
  safeSetLocalStorage('lang', normalized)
  initVantLocale()
}

// 安全设置localStorage
const safeSetLocalStorage = (key, value) => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.setItem(key, value)
    }
  } catch (error) {
    console.warn('localStorage设置失败:', error)
  }
}

export default i18n
